import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import useSiteMetadata from "../hooks/useSiteMetadata"
import useGlobal from "../context/GlobalContext"
import normalizeUrl from "normalize-url"

const SEO = ({
  desc,
  lang,
  meta,
  title,
  pathname,
  keywords,
  bannerImage,
  children,
}) => {
  const { languageOptions, currentLang, currentRouteOption } = useGlobal()
  const siteMetadata = useSiteMetadata()
  const metaDescription = desc || siteMetadata.description
  const metaTitle = title || siteMetadata.title
  const metaKeywords = keywords || siteMetadata.metakeywords
  const ogBannerImage = bannerImage || siteMetadata.banner
  const pageLink =
    `${normalizeUrl(
      `${siteMetadata.siteUrl}${
        currentLang === "en-IN" || currentLang === "en-in"
          ? "/"
          : `/${currentLang}/`
      }${pathname}`,
      { stripWWW: false }
    )}/` || siteMetadata.siteUrl
  return (
    <Helmet
      htmlAttributes={{
        lang: currentRouteOption?.lang ?? lang,
      }}
      title={metaTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: metaKeywords,
        },
        {
          name: `copyright`,
          content: `© 2024 Motherhost`,
        },
        {
          property: `og:title`,
          content: metaTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `product`,
        },
        {
          property: `og:url`,
          content: pageLink,
        },
        {
          property: `og:image`,
          content: `${siteMetadata.siteUrl}${ogBannerImage}`,
        },
        {
          property: `og:site_name`,
          content: `Motherhost`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:site`,
          content: `@Motherhost`,
        },
        {
          name: `twitter:creator`,
          content: siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: metaTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:url`,
          content: pageLink,
        },
        {
          name: `twitter:image:src`,
          content: `${siteMetadata.siteUrl}${ogBannerImage}`,
        },
      ].concat(meta)}
    >
      <link rel="canonical" href={pageLink} />
      {languageOptions.map(({ value, lang }) => (
        <link
          key={value}
          rel="alternate"
          href={`${normalizeUrl(
            `https://www.motherhost.com${
              value === "en-in" ? "/" : `/${value}/`
            }${pathname}`,
            { stripWWW: false }
          )}/`}
          hreflang={lang}
        />
      ))}
      {children}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en-IN`,
  meta: [],
  description: ``,
  pathname: ``,
  keywords: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  pathname: PropTypes.string,
  keywords: PropTypes.string,
}

export default SEO
